'use client';
import React, { useState } from 'react';
import Image from 'next/image';

interface GalleryItem {
  id: number;
  src: string;
  alt: string;
  caption?: string;
  category: string;
}

const galleryItems: GalleryItem[] = [
{
  id: 8,
  src: 'https://www.zenyoganepal.com/api/media/file/ilam-tefsdfsdfsfa-garden-2077.jpg?prefix=uploads',
  alt: 'Nepal tea gardens',
  category: 'location'
},
{
  id: 7,
  src: 'https://ww.zenyoganepal.com/api/media/file/himalaya_mountainssrsdfsdfsd_nepal_by_citizenfresh_d10beij-fullview.jpg?prefix=uploads',
  alt: 'Himalaya mountains Nepal',
  category: 'location'
},
{
  id: 6,
  src: 'https://www.zenyoganepal.com/api/media/file/vlcsnakml%3Bml%3Bmlml%3Bmlp-00007.png?prefix=uploads',
  alt: 'Yoga practice - Yoga Bowls',
  category: 'practice'
},
{
  id: 1,
  src: 'https://www.zenyoganepal.com/api/media/file/vlcsnap-00003.png?prefix=uploads',
  alt: 'Yoga practice 3',
  caption: 'Yoga practice 3',
  category: 'practice'
},
{
  id: 4,
  src: 'https://www.zenyoganepal.com/api/media/file/unnamed%20(3).webp?prefix=uploads',
  alt: 'Studio Space - right in the heart of Thamel.',
  caption: 'Studio Space - right in the heart of Thamel.',
  category: 'community'
}];


const categories = [
{ label: 'Show All', value: 'all' },
{ label: 'Yoga Practice', value: 'practice' },
{ label: 'Accommodation & Dining', value: 'accommodation' },
{ label: 'Himalayan Surroundings', value: 'location' },
{ label: 'Community', value: 'community' }];


interface LightboxProps {
  item: GalleryItem;
  onClose: () => void;
}

function Lightbox({ item, onClose }: LightboxProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-forest-dark/90 backdrop-blur-sm p-4"
      onClick={onClose}>
      
      <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
          aria-label="Close lightbox">
          
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" /><path d="m6 6 12 12" />
          </svg>
        </button>
        <div className="relative aspect-video overflow-hidden rounded-2xl">
          <Image
            src={item.src}
            alt={item.alt}
            fill
            unoptimized
            className="object-contain" />
          
        </div>
        {item.caption &&
        <p className="mt-3 text-center text-sm text-cream/80 font-sans">{item.caption}</p>
        }
      </div>
    </div>);

}

export default function GallerySection() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);

  const filtered = activeCategory === 'all' ?
  galleryItems :
  galleryItems.filter((item) => item.category === activeCategory);

  return (
    <section id="see-our-world" className="relative scroll-mt-24 overflow-hidden bg-cream py-20">
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-cream-dark/20 to-transparent -z-10 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-saffron-dark uppercase font-bold tracking-widest text-xs md:text-sm border-b-2 border-saffron-light pb-1">
            Our Gallery
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-forest font-bold mt-3 mb-4 leading-tight">
            SEE OUR WORLD
          </h2>
          <p className="font-sans text-charcoal/70 text-sm md:text-base leading-relaxed">
            Imagine beginning your morning with Himalayan sunrises, practicing yoga in peaceful surroundings, sharing nourishing, organic meals with friends from around the world, and ending each day immersed in meditation and the resonance of singing bowls.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) =>
          <button
            key={cat.value}
            aria-pressed={activeCategory === cat.value}
            onClick={() => setActiveCategory(cat.value)}
            className={`font-sans text-xs md:text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-300 ${
            activeCategory === cat.value ?
            'bg-forest text-cream shadow-md' :
            'bg-cream-dark/30 hover:bg-cream-dark/60 text-forest'}`
            }>
            
              {cat.label}
            </button>
          )}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((item) =>
          <div
            key={item.id}
            className="group relative aspect-square rounded-2xl overflow-hidden shadow-md cursor-pointer border border-forest/5"
            onClick={() => setLightboxItem(item)}>
            
              <Image
              src={item.src}
              alt={item.alt}
              fill
              unoptimized
              className="object-cover group-hover:scale-110 transition-transform duration-500" />
            
              <div className="absolute inset-0 bg-forest-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="bg-cream/90 text-forest p-3 rounded-full shadow-lg hover:scale-105 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" x2="16.65" y1="21" y2="16.65" />
                    <line x1="11" x2="11" y1="8" y2="14" />
                    <line x1="8" x2="14" y1="11" y2="11" />
                  </svg>
                </div>
              </div>
              {item.caption &&
            <div className="absolute bottom-3 left-3 right-3 bg-charcoal/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-xl text-[10px] md:text-xs text-left truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {item.caption}
                </div>
            }
            </div>
          )}
        </div>

        {/* Quote */}
        <div className="mt-16 text-center max-w-2xl mx-auto border-t border-forest/10 pt-10">
          <p className="font-serif italic text-charcoal/80 text-lg md:text-xl leading-relaxed mb-2">
            &ldquo;When you change the way you look at things, the things you look at change.&rdquo;
          </p>
          <p className="font-sans font-bold uppercase tracking-wider text-xs text-forest/70">
            &mdash; Wayne Dyer
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxItem &&
      <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
      }
    </section>);

}